import React, {useState, useEffect} from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  Alert,
  Chip,
  LinearProgress,
  Tabs,
  Tab,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  TrendingUp,
  Water,
  Warning,
  Schedule,
  Analytics,
  Refresh,
  ExpandMore,
  CheckCircle,
  Error as ErrorIcon,
  Info,
  Timeline,
  Assessment,
  Science,
  Psychology,
  Engineering,
  MonetizationOn,
  Build,
  ShowChart,
  Timeline as TimelineIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon2,
} from "@mui/icons-material";
import {useTranslation} from "react-i18next";
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  ScatterChart,
  Scatter,
} from "recharts";
import {useAuth} from "../../contexts/AuthContext";
import {trackFeatureAccess} from "../../utils/analytics";
import {fetchAnimalGroups} from "../../api/animal";
import {fetchPonds} from "../../api/pond";
import {fetchFarms} from "../../api/farm";
import {
  advancedGrowthForecasting,
  advancedWaterQualityAnalysis,
  predictiveMaintenanceAnalysis,
  marketPricePrediction,
  AdvancedGrowthForecastingResult,
  AdvancedWaterQualityResult,
  PredictiveMaintenanceResult,
  MarketPricePredictionResult,
} from "../../api/predictive";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const {children, value, index, ...other} = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`advanced-predictive-tabpanel-${index}`}
      aria-labelledby={`advanced-predictive-tab-${index}`}
      {...other}>
      {value === index && <Box sx={{p: 3}}>{children}</Box>}
    </div>
  );
}

const AdvancedPredictivePage: React.FC = () => {
  const {t} = useTranslation();
  const {user} = useAuth();
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [animalGroups, setAnimalGroups] = useState<any[]>([]);
  const [ponds, setPonds] = useState<any[]>([]);
  const [farms, setFarms] = useState<any[]>([]);
  const [selectedAnimalGroup, setSelectedAnimalGroup] = useState<string>("");
  const [selectedPond, setSelectedPond] = useState<string>("");
  const [daysAhead, setDaysAhead] = useState<number>(90);
  const [error, setError] = useState<string>("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogContent, setDialogContent] = useState<{title: string; content: React.ReactNode}>({title: "", content: null});

  // Results state
  const [growthForecast, setGrowthForecast] = useState<AdvancedGrowthForecastingResult | null>(null);
  const [waterQualityAnalysis, setWaterQualityAnalysis] = useState<AdvancedWaterQualityResult | null>(null);
  const [maintenanceAnalysis, setMaintenanceAnalysis] = useState<PredictiveMaintenanceResult | null>(null);
  const [marketPrediction, setMarketPrediction] = useState<MarketPricePredictionResult | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [animals, pondsData, farmsData] = await Promise.all([fetchAnimalGroups(), fetchPonds(), fetchFarms()]);
      setAnimalGroups(animals);
      setPonds(pondsData);
      setFarms(farmsData);
    } catch (error) {
      console.error("Error loading data:", error);
      setError("Failed to load data");
    }
  };

  const advancedTabNames = [
    "growth_forecasting",
    "water_quality",
    "predictive_maintenance",
    "market_analysis",
  ];

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    trackFeatureAccess(`advanced_predictive_tab_${advancedTabNames[newValue] || "unknown"}`, {
      tab_index: newValue,
    });
  };

  const runAdvancedGrowthForecasting = async () => {
    if (!selectedAnimalGroup) {
      setError("Please select an animal group");
      return;
    }

    setLoading(true);
    setError("");
    try {
      const data = await advancedGrowthForecasting(selectedAnimalGroup, daysAhead);
      setGrowthForecast(data);
    } catch (error) {
      setError("Failed to get advanced growth forecasting");
      console.error("Advanced growth forecasting error:", error);
    } finally {
      setLoading(false);
    }
  };

  const runAdvancedWaterQualityAnalysis = async () => {
    if (!selectedPond) {
      setError("Please select a pond");
      return;
    }

    setLoading(true);
    setError("");
    try {
      const data = await advancedWaterQualityAnalysis(selectedPond, daysAhead);
      setWaterQualityAnalysis(data);
    } catch (error) {
      setError("Failed to get advanced water quality analysis");
      console.error("Advanced water quality analysis error:", error);
    } finally {
      setLoading(false);
    }
  };

  const runPredictiveMaintenanceAnalysis = async () => {
    if (!selectedPond) {
      setError("Please select a pond");
      return;
    }

    setLoading(true);
    setError("");
    try {
      const data = await predictiveMaintenanceAnalysis(selectedPond);
      setMaintenanceAnalysis(data);
    } catch (error) {
      setError("Failed to get predictive maintenance analysis");
      console.error("Predictive maintenance analysis error:", error);
    } finally {
      setLoading(false);
    }
  };

  const runMarketPricePrediction = async () => {
    if (!selectedAnimalGroup) {
      setError("Please select an animal group");
      return;
    }

    const animalGroup = animalGroups.find((ag) => ag.id === selectedAnimalGroup);
    if (!animalGroup) {
      setError("Animal group not found");
      return;
    }

    setLoading(true);
    setError("");
    try {
      const harvestDate = new Date();
      harvestDate.setDate(harvestDate.getDate() + 30); // Default to 30 days from now
      const data = await marketPricePrediction(animalGroup.species, harvestDate.toISOString().split("T")[0]);
      setMarketPrediction(data);
    } catch (error) {
      setError("Failed to get market price prediction");
      console.error("Market price prediction error:", error);
    } finally {
      setLoading(false);
    }
  };

  const getRiskColor = (riskLevel: string) => {
    switch (riskLevel) {
      case "low":
        return "success";
      case "medium":
        return "warning";
      case "high":
        return "error";
      case "critical":
        return "error";
      default:
        return "default";
    }
  };

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 0.8) return "success";
    if (confidence >= 0.6) return "warning";
    return "error";
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "good":
        return "success";
      case "warning":
        return "warning";
      case "critical":
        return "error";
      default:
        return "default";
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "good":
        return <CheckCircleIcon color="success" />;
      case "warning":
        return <WarningIcon color="warning" />;
      case "critical":
        return <ErrorIcon2 color="error" />;
      default:
        return <Info color="info" />;
    }
  };

  const showDetailedAnalysis = (title: string, content: React.ReactNode) => {
    setDialogContent({title, content});
    setDialogOpen(true);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <Box sx={{p: 3}}>
      <Typography variant="h4" gutterBottom>
        <Science sx={{mr: 2, verticalAlign: "middle"}} />
        Advanced Predictive Analytics
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{mb: 3}}>
        Leverage machine learning algorithms for sophisticated forecasting, anomaly detection, and predictive maintenance
      </Typography>

      {error && (
        <Alert severity="error" sx={{mb: 2}}>
          {error}
        </Alert>
      )}

      <Paper sx={{width: "100%", mb: 3}}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="advanced predictive analytics tabs">
          <Tab icon={<TrendingUp />} label="Growth Forecasting" />
          <Tab icon={<Water />} label="Water Quality" />
          <Tab icon={<Build />} label="Predictive Maintenance" />
          <Tab icon={<MonetizationOn />} label="Market Analysis" />
        </Tabs>
      </Paper>

      <TabPanel value={tabValue} index={0}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Card>
              <CardHeader title="Growth Forecasting Configuration" />
              <CardContent>
                <FormControl fullWidth sx={{mb: 2}}>
                  <InputLabel>Animal Group</InputLabel>
                  <Select value={selectedAnimalGroup} onChange={(e) => setSelectedAnimalGroup(e.target.value)} label="Animal Group">
                    {animalGroups.map((group) => (
                      <MenuItem key={group.id} value={group.id}>
                        {group.name} ({group.species})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  fullWidth
                  label="Days to Forecast"
                  type="number"
                  value={daysAhead}
                  onChange={(e) => setDaysAhead(Number(e.target.value))}
                  sx={{mb: 2}}
                />
                <Button
                  fullWidth
                  variant="contained"
                  onClick={runAdvancedGrowthForecasting}
                  disabled={loading || !selectedAnimalGroup}
                  startIcon={<Analytics />}>
                  Run Advanced Forecasting
                </Button>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={8}>
            {loading && <LinearProgress sx={{mb: 2}} />}

            {growthForecast && (
              <Card>
                <CardHeader
                  title="Advanced Growth Forecasting Results"
                  action={
                    <IconButton onClick={() => setGrowthForecast(null)}>
                      <Refresh />
                    </IconButton>
                  }
                />
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="h6" gutterBottom>
                        Model Performance
                      </Typography>
                      <Typography variant="body2">
                        Model: {growthForecast.forecast.model.name} v{growthForecast.forecast.model.version}
                      </Typography>
                      <Typography variant="body2">Accuracy: {growthForecast.forecast.model.accuracy.toFixed(3)}</Typography>
                      <Typography variant="body2">Last Trained: {formatDate(growthForecast.forecast.model.lastTrained)}</Typography>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Typography variant="h6" gutterBottom>
                        Anomaly Detection
                      </Typography>
                      <Typography variant="body2">Anomalies Detected: {growthForecast.anomalies.filter((a) => a.isAnomaly).length}</Typography>
                      <Typography variant="body2">High Severity: {growthForecast.anomalies.filter((a) => a.severity === "high").length}</Typography>
                    </Grid>
                  </Grid>

                  <Divider sx={{my: 2}} />

                  <Typography variant="h6" gutterBottom>
                    Forecast Chart
                  </Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={growthForecast.forecast.predictions}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="timestamp" tickFormatter={(value) => new Date(value).toLocaleDateString()} />
                      <YAxis />
                      <RechartsTooltip
                        labelFormatter={(value) => new Date(value).toLocaleDateString()}
                        formatter={(value: any) => [`${value}g`, "Predicted Weight"]}
                      />
                      <Legend />
                      <Line type="monotone" dataKey="value" stroke="#8884d8" strokeWidth={2} dot={false} name="Predicted Weight" />
                    </LineChart>
                  </ResponsiveContainer>

                  <Divider sx={{my: 2}} />

                  <Typography variant="h6" gutterBottom>
                    Insights & Recommendations
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle1" gutterBottom>
                        Insights
                      </Typography>
                      <List dense>
                        {growthForecast.insights.map((insight, index) => (
                          <ListItem key={index}>
                            <ListItemIcon>
                              <Info color="info" />
                            </ListItemIcon>
                            <ListItemText primary={insight} />
                          </ListItem>
                        ))}
                      </List>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle1" gutterBottom>
                        Recommendations
                      </Typography>
                      <List dense>
                        {growthForecast.recommendations.map((rec, index) => (
                          <ListItem key={index}>
                            <ListItemIcon>
                              <CheckCircle color="primary" />
                            </ListItemIcon>
                            <ListItemText primary={rec} />
                          </ListItem>
                        ))}
                      </List>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            )}
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Card>
              <CardHeader title="Water Quality Analysis Configuration" />
              <CardContent>
                <FormControl fullWidth sx={{mb: 2}}>
                  <InputLabel>Pond</InputLabel>
                  <Select value={selectedPond} onChange={(e) => setSelectedPond(e.target.value)} label="Pond">
                    {ponds.map((pond) => (
                      <MenuItem key={pond.id} value={pond.id}>
                        {pond.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  fullWidth
                  label="Days to Forecast"
                  type="number"
                  value={daysAhead}
                  onChange={(e) => setDaysAhead(Number(e.target.value))}
                  sx={{mb: 2}}
                />
                <Button
                  fullWidth
                  variant="contained"
                  onClick={runAdvancedWaterQualityAnalysis}
                  disabled={loading || !selectedPond}
                  startIcon={<Analytics />}>
                  Run Water Quality Analysis
                </Button>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={8}>
            {loading && <LinearProgress sx={{mb: 2}} />}

            {waterQualityAnalysis && (
              <Card>
                <CardHeader
                  title="Advanced Water Quality Analysis Results"
                  action={
                    <IconButton onClick={() => setWaterQualityAnalysis(null)}>
                      <Refresh />
                    </IconButton>
                  }
                />
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="h6" gutterBottom>
                        Risk Assessment
                      </Typography>
                      <Chip
                        label={`Risk Level: ${waterQualityAnalysis.riskAssessment.overallRisk.toUpperCase()}`}
                        color={getRiskColor(waterQualityAnalysis.riskAssessment.overallRisk)}
                        sx={{mb: 1}}
                      />
                      <Typography variant="body2">Risk Score: {waterQualityAnalysis.riskAssessment.riskScore}</Typography>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Typography variant="h6" gutterBottom>
                        Anomaly Detection
                      </Typography>
                      <Typography variant="body2">Total Anomalies: {waterQualityAnalysis.anomalies.length}</Typography>
                      <Typography variant="body2">
                        High Severity: {waterQualityAnalysis.anomalies.filter((a) => a.severity === "high").length}
                      </Typography>
                    </Grid>
                  </Grid>

                  <Divider sx={{my: 2}} />

                  <Typography variant="h6" gutterBottom>
                    Water Quality Forecasts
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <Typography variant="subtitle1" gutterBottom>
                        Temperature Forecast
                      </Typography>
                      <ResponsiveContainer width="100%" height={200}>
                        <LineChart data={waterQualityAnalysis.temperatureForecast.predictions}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="timestamp" tickFormatter={(value) => new Date(value).toLocaleDateString()} />
                          <YAxis />
                          <RechartsTooltip
                            labelFormatter={(value) => new Date(value).toLocaleDateString()}
                            formatter={(value: any) => [`${value}°C`, "Temperature"]}
                          />
                          <Line type="monotone" dataKey="value" stroke="#ff7300" strokeWidth={2} dot={false} />
                        </LineChart>
                      </ResponsiveContainer>
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <Typography variant="subtitle1" gutterBottom>
                        pH Forecast
                      </Typography>
                      <ResponsiveContainer width="100%" height={200}>
                        <LineChart data={waterQualityAnalysis.phForecast.predictions}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="timestamp" tickFormatter={(value) => new Date(value).toLocaleDateString()} />
                          <YAxis />
                          <RechartsTooltip
                            labelFormatter={(value) => new Date(value).toLocaleDateString()}
                            formatter={(value: any) => [value, "pH"]}
                          />
                          <Line type="monotone" dataKey="value" stroke="#00ff00" strokeWidth={2} dot={false} />
                        </LineChart>
                      </ResponsiveContainer>
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <Typography variant="subtitle1" gutterBottom>
                        Dissolved Oxygen Forecast
                      </Typography>
                      <ResponsiveContainer width="100%" height={200}>
                        <LineChart data={waterQualityAnalysis.dissolvedOxygenForecast.predictions}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="timestamp" tickFormatter={(value) => new Date(value).toLocaleDateString()} />
                          <YAxis />
                          <RechartsTooltip
                            labelFormatter={(value) => new Date(value).toLocaleDateString()}
                            formatter={(value: any) => [`${value} mg/L`, "Dissolved Oxygen"]}
                          />
                          <Line type="monotone" dataKey="value" stroke="#0080ff" strokeWidth={2} dot={false} />
                        </LineChart>
                      </ResponsiveContainer>
                    </Grid>
                  </Grid>

                  <Divider sx={{my: 2}} />

                  <Typography variant="h6" gutterBottom>
                    Recommendations
                  </Typography>
                  <List>
                    {waterQualityAnalysis.recommendations.map((rec, index) => (
                      <ListItem key={index}>
                        <ListItemIcon>
                          <CheckCircle color="primary" />
                        </ListItemIcon>
                        <ListItemText primary={rec} />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            )}
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Card>
              <CardHeader title="Predictive Maintenance Configuration" />
              <CardContent>
                <FormControl fullWidth sx={{mb: 2}}>
                  <InputLabel>Pond</InputLabel>
                  <Select value={selectedPond} onChange={(e) => setSelectedPond(e.target.value)} label="Pond">
                    {ponds.map((pond) => (
                      <MenuItem key={pond.id} value={pond.id}>
                        {pond.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={runPredictiveMaintenanceAnalysis}
                  disabled={loading || !selectedPond}
                  startIcon={<Build />}>
                  Run Maintenance Analysis
                </Button>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={8}>
            {loading && <LinearProgress sx={{mb: 2}} />}

            {maintenanceAnalysis && (
              <Card>
                <CardHeader
                  title="Predictive Maintenance Analysis Results"
                  action={
                    <IconButton onClick={() => setMaintenanceAnalysis(null)}>
                      <Refresh />
                    </IconButton>
                  }
                />
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="h6" gutterBottom>
                        Equipment Health Status
                      </Typography>
                      <List>
                        {Object.entries(maintenanceAnalysis.equipmentHealth).map(([equipment, health]) => (
                          <ListItem key={equipment}>
                            <ListItemIcon>{getStatusIcon(health.status)}</ListItemIcon>
                            <ListItemText
                              primary={`${equipment.charAt(0).toUpperCase() + equipment.slice(1)}`}
                              secondary={
                                <Box>
                                  <Typography variant="body2">Status: {health.status.toUpperCase()}</Typography>
                                  <Typography variant="body2">Next Maintenance: {formatDate(health.nextMaintenance)}</Typography>
                                  <Typography variant="body2">Confidence: {(health.confidence * 100).toFixed(1)}%</Typography>
                                </Box>
                              }
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Typography variant="h6" gutterBottom>
                        Water Quality Trends
                      </Typography>
                      <Chip
                        label={`Trend: ${maintenanceAnalysis.waterQualityTrends.trend.toUpperCase()}`}
                        color={
                          maintenanceAnalysis.waterQualityTrends.trend === "improving"
                            ? "success"
                            : maintenanceAnalysis.waterQualityTrends.trend === "stable"
                            ? "warning"
                            : "error"
                        }
                        sx={{mb: 1}}
                      />
                      <Typography variant="h6" gutterBottom>
                        Trend Factors
                      </Typography>
                      <List dense>
                        {maintenanceAnalysis.waterQualityTrends.factors.map((factor, index) => (
                          <ListItem key={index}>
                            <ListItemIcon>
                              <Info color="info" />
                            </ListItemIcon>
                            <ListItemText primary={factor} />
                          </ListItem>
                        ))}
                      </List>
                    </Grid>
                  </Grid>

                  <Divider sx={{my: 2}} />

                  <Typography variant="h6" gutterBottom>
                    Maintenance Recommendations
                  </Typography>
                  <List>
                    {maintenanceAnalysis.recommendations.map((rec, index) => (
                      <ListItem key={index}>
                        <ListItemIcon>
                          <CheckCircle color="primary" />
                        </ListItemIcon>
                        <ListItemText primary={rec} />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            )}
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={tabValue} index={3}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Card>
              <CardHeader title="Market Analysis Configuration" />
              <CardContent>
                <FormControl fullWidth sx={{mb: 2}}>
                  <InputLabel>Animal Group</InputLabel>
                  <Select value={selectedAnimalGroup} onChange={(e) => setSelectedAnimalGroup(e.target.value)} label="Animal Group">
                    {animalGroups.map((group) => (
                      <MenuItem key={group.id} value={group.id}>
                        {group.name} ({group.species})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={runMarketPricePrediction}
                  disabled={loading || !selectedAnimalGroup}
                  startIcon={<MonetizationOn />}>
                  Run Market Analysis
                </Button>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={8}>
            {loading && <LinearProgress sx={{mb: 2}} />}

            {marketPrediction && (
              <Card>
                <CardHeader
                  title="Market Price Prediction Results"
                  action={
                    <IconButton onClick={() => setMarketPrediction(null)}>
                      <Refresh />
                    </IconButton>
                  }
                />
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="h6" gutterBottom>
                        Price Prediction
                      </Typography>
                      <Typography variant="h4" color="primary" gutterBottom>
                        ${marketPrediction.predictedPrice}/kg
                      </Typography>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Range: ${marketPrediction.priceRange.min} - ${marketPrediction.priceRange.max}/kg
                      </Typography>
                      <Chip
                        label={`Confidence: ${(marketPrediction.confidence * 100).toFixed(1)}%`}
                        color={marketPrediction.confidence > 0.8 ? "success" : marketPrediction.confidence > 0.6 ? "warning" : "error"}
                        sx={{mb: 2}}
                      />

                      <Typography variant="h6" gutterBottom>
                        Market Trends
                      </Typography>
                      <List dense>
                        {marketPrediction.marketTrends.map((trend, index) => (
                          <ListItem key={index}>
                            <ListItemIcon>
                              <TrendingUp color="primary" />
                            </ListItemIcon>
                            <ListItemText primary={trend} />
                          </ListItem>
                        ))}
                      </List>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Typography variant="h6" gutterBottom>
                        Price Factors
                      </Typography>
                      <List dense>
                        {marketPrediction.factors.map((factor, index) => (
                          <ListItem key={index}>
                            <ListItemIcon>
                              <Info color="info" />
                            </ListItemIcon>
                            <ListItemText primary={factor} />
                          </ListItem>
                        ))}
                      </List>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            )}
          </Grid>
        </Grid>
      </TabPanel>
    </Box>
  );
};

export default AdvancedPredictivePage;
